import { Check, Pencil, Pin, RefreshCw } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useArtifactsLabel } from './agentName'
import { AgentTab, TAB_LABEL } from './AgentTabBar'
import { CAPABILITIES, CAPABILITY_ORDER, overviewTarget } from './capabilities'
import { PinStore, usePinStore } from './pinnedArtifacts'

/** Squares standing in for whatever the agent built — a chart, a table, a COI. */
const ARTIFACTS = [
  { title: 'Cash forecast', refreshed: 'October 8, 2026 7:05am' },
  { title: 'Profit per truck', refreshed: 'October 7, 2026 4:12pm' },
  { title: 'Open AR aging', refreshed: 'October 6, 2026 11:48am' },
  { title: 'Driver settlements', refreshed: 'October 2, 2026 9:30am' },
]

/** Only a product the fleet has turned on has a page to pin something to. */
const PIN_TARGETS = CAPABILITY_ORDER.filter((entry) => CAPABILITIES[entry].enabled)

export function ArtifactsTab({
  onEdit,
  contentWidth,
}: {
  onEdit?: (label: string) => void
  contentWidth?: number
}) {
  const pins = usePinStore()
  const artifacts = useArtifactsLabel()

  return (
    <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto">
      <div
        className="mx-auto flex w-full flex-col gap-3 p-3"
        style={contentWidth ? { maxWidth: contentWidth } : undefined}
      >
        <h2 className="text-ink text-sm font-bold tracking-tight">
          {artifacts ?? TAB_LABEL[AgentTab.ARTIFACTS]}
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {ARTIFACTS.map((artifact) => (
            <ArtifactCard key={artifact.title} {...artifact} onEdit={onEdit} pins={pins} />
          ))}
        </div>
      </div>
    </div>
  )
}

function ArtifactCard({
  title,
  refreshed,
  onEdit,
  pins,
}: {
  title: string
  refreshed: string
  onEdit?: (title: string) => void
  pins: PinStore
}) {
  const pinnedTo = pins.pinnedTo(title)

  return (
    <div className="flex flex-col gap-1">
      <div className="border-ink-100 bg-ink-50 relative aspect-square rounded-md border">
        <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => onEdit?.(title)}
            aria-label={`Edit ${title}`}
            className="hover:bg-ink-50 text-ink-400 hover:text-ink rounded p-1"
          >
            <Pencil className="size-3" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={pinnedTo ? `Pinned to ${pinnedTo}` : 'Pin this artifact'}
              className={cn(
                'hover:bg-ink-50 rounded p-1',
                pinnedTo ? 'text-brand-700' : 'text-ink-400 hover:text-ink'
              )}
            >
              <Pin className={cn('size-3', pinnedTo && 'fill-current')} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="text-[10px]">Pin a copy to</DropdownMenuLabel>
              {PIN_TARGETS.map((entry) => {
                const target = overviewTarget(entry)
                const selected = pinnedTo === target
                return (
                  <DropdownMenuItem
                    key={entry}
                    onSelect={() => pins.setPin(title, selected ? null : target)}
                    className="justify-between gap-2 text-xs"
                  >
                    {target}
                    {selected && <Check className="text-brand-700 size-3.5" />}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex items-start justify-between gap-1 px-0.5">
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-ink truncate text-[11px] font-medium">{title}</span>
          <span className="text-ink-400 truncate text-[9px]">Last refreshed {refreshed}</span>
          {pinnedTo && <span className="text-ink-500 truncate text-[9px]">Pinned to {pinnedTo}</span>}
        </span>
        <button
          type="button"
          aria-label={`Refresh ${title}`}
          className="hover:bg-ink-50 text-ink-400 hover:text-ink -mt-0.5 shrink-0 rounded p-1"
        >
          <RefreshCw className="size-3" />
        </button>
      </div>
    </div>
  )
}
